// Display rules for Hantera > Dubbletter, the add-lead duplicate notice and
// the import preview's name matches. Pure, so the tests read them directly.
import { ApiError } from '@/shared/api/core'
import { fmtDate, fmtNum } from '@/shared/lib/format'
import { stageLabel } from '@/shared/pipelineColors'
import type {
  DuplicatePair,
  DuplicateSide,
  DuplicateSource,
  ImportReport,
  MergeChoice,
  MergeChoices,
  MergeIds,
  MergeLoss,
  MergeMoves,
  MergePreview,
  MergeValue,
  ResolveItem,
  ResolveStatus,
} from '@/features/admin/api'

export type DuplicateFilter = 'all' | 'org' | 'name'

export const KIND_LABEL: Record<DuplicatePair['kind'], string> = {
  org: 'Samma org.nr',
  name: 'Samma namn',
}

export function filterPairs(pairs: DuplicatePair[], filter: DuplicateFilter): DuplicatePair[] {
  return filter === 'all' ? pairs : pairs.filter(p => p.kind === filter)
}

export function duplicatesHeadline(pairs: number, withoutOrg: number): string {
  const leads = withoutOrg === 1 ? 'lead saknar' : 'leads saknar'
  return `${pairs} par att granska · ${withoutOrg} ${leads} organisationsnummer`
}

const DIFFERENT_ENTITIES = 'Olika bolag, slås inte ihop'

export interface PairView {
  kind: string
  mergeable: boolean
  /** Why the pair cannot merge, or null when it can. */
  blocked: string | null
  keep: 'a' | 'b'
}

export function pairView(pair: DuplicatePair): PairView {
  return {
    kind: KIND_LABEL[pair.kind],
    mergeable: pair.mergeable,
    blocked: pair.mergeable
      ? null
      : pair.reason === 'different_legal_entities' ? DIFFERENT_ENTITIES : 'Kan inte slås ihop',
    keep: pair.suggestedKeepId === pair.b.companyId ? 'b' : 'a',
  }
}

/** The preview request: the suggested keeper, or the other side when `keepOther`. */
export function mergeIds(pair: DuplicatePair, keepOther: boolean): MergeIds {
  const suggested = pair.suggestedKeepId === pair.b.companyId ? pair.b : pair.a
  const other = suggested === pair.a ? pair.b : pair.a
  const keep = keepOther ? other : suggested
  const drop = keepOther ? suggested : other
  return { keepId: keep.companyId, dropId: drop.companyId }
}

export function ownerName(owner: { email: string; name?: string | null } | string | null | undefined): string {
  if (!owner) return 'Ingen ägare'
  if (typeof owner === 'string') return owner
  return owner.name || owner.email
}

/** A merge with the other side kept. */
export function swapIds(ids: MergeIds): MergeIds {
  return { ...ids, keepId: ids.dropId, dropId: ids.keepId }
}

/** The "Behålls" or "Försvinner" label on one side of a pair. */
export function sideRole(side: DuplicateSide, keepId: string): 'Behålls' | 'Försvinner' {
  return side.companyId === keepId ? 'Behålls' : 'Försvinner'
}

const SOURCE_LABEL: Record<DuplicateSource, string> = {
  radar: 'Radar',
  import: 'Importlista',
  manual: 'Manuellt tillagd',
  salesforce: 'Salesforce',
  other: 'Annan källa',
}

/** The server's Swedish label, else one from the source code. */
export function sourceText(side: Pick<DuplicateSide, 'source' | 'sourceLabel'>): string {
  if (side.sourceLabel?.trim()) return side.sourceLabel.trim()
  return (side.source && SOURCE_LABEL[side.source]) || 'Okänd källa'
}

/** The facts a pair card lists for one side, in display order. */
export function sideFacts(side: DuplicateSide): [string, string][] {
  return [
    ['Org.nr', side.orgNo?.trim() || 'saknas'],
    ['Ägare', ownerName(side.owner)],
    ['Stadie', stageLabel(side.stage)],
    ['Senaste aktivitet', fmtDate(side.lastActivityAt)],
    ['Samtal', String(side.calls ?? 0)],
    ['Möten', String(side.meetings ?? 0)],
    ['Kontakter', String(side.contacts ?? 0)],
    ['Källa', sourceText(side)],
  ]
}

// ── Vad händer ──────────────────────────────────────────────────────────────

/** A loss or fill value as text: empty reads "saknas", numbers in Swedish, booleans Ja/Nej. */
export function valueText(value: MergeValue | undefined): string {
  if (value === null || value === undefined) return 'saknas'
  if (typeof value === 'number') return fmtNum(value)
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nej'
  return String(value).trim() || 'saknas'
}

const hasValue = (value: MergeValue | undefined) => valueText(value) !== 'saknas'

export interface LossRow {
  key: string
  loss: MergeLoss
  /** The side the kept company ends up with, or null when the row is not decided by side. */
  choice: MergeChoice | null
  /** The value the kept company ends up with. */
  kept: string
  /** The value that disappears. */
  gone: string
  /** Both sides hold a value and the person may pick one. */
  toggle: boolean
}

/**
 * The Försvinner rows, conflicts first, each read with its current choice: a
 * "drop" choice swaps which value stays and which disappears. A row without a
 * choice (stage, AI score, the company record) reads as its sentence.
 */
export function lossRows(losses: MergeLoss[], picked: MergeChoices): LossRow[] {
  return losses
    .map((loss, index) => ({ loss, index }))
    .sort((a, b) => Number(b.loss.conflict) - Number(a.loss.conflict) || a.index - b.index)
    .map(({ loss, index }) => {
      const choice = (loss.choosable && picked[loss.field]) || loss.choice || null
      const keepValue = valueText(loss.keepValue)
      const dropValue = valueText(loss.dropValue)
      return {
        key: `${loss.field}:${index}`,
        loss,
        choice,
        kept: choice === 'drop' ? dropValue : keepValue,
        gone: choice === 'drop' ? keepValue : dropValue,
        toggle: loss.choosable && hasValue(loss.keepValue) && hasValue(loss.dropValue),
      }
    })
}

/**
 * The merge request: the ids and, when the person changed a choosable field,
 * those choices. Untouched fields go unnamed and take the default.
 */
export function mergeBody(ids: MergeIds, picked: MergeChoices): MergeIds {
  const choices = Object.fromEntries(Object.entries(picked).filter(([, side]) => side === 'keep' || side === 'drop'))
  const { choices: _ignored, ...rest } = ids
  return Object.keys(choices).length ? { ...rest, choices } : rest
}

const MOVE_LABEL: [keyof MergeMoves, string][] = [
  ['contacts', 'kontakter'],
  ['activities', 'aktiviteter'],
  ['verdicts', 'AI-bedömningar'],
  ['notes', 'anteckningar'],
  ['timelineEntries', 'tidslinjeposter'],
  ['skips', 'hoppa över-markeringar'],
  ['usageEvents', 'användningshändelser'],
]

// The first four always show; the rest only when something moves.
const ALWAYS_SHOWN = 4

export function moveLines(moves: MergeMoves): [string, number][] {
  return MOVE_LABEL
    .filter(([key], index) => index < ALWAYS_SHOWN || (moves[key] ?? 0) > 0)
    .map(([key, label]) => [label, moves[key] ?? 0])
}

/** What a past merge moved, without the zero counts. */
export function movesSummary(moves: Partial<MergeMoves> | string | null | undefined): string {
  if (typeof moves === 'string') return moves || 'Inget'
  if (!moves) return 'Inget'
  const lines = MOVE_LABEL.filter(([key]) => (moves[key] ?? 0) > 0).map(([key, label]) => `${moves[key]} ${label}`)
  return lines.length ? lines.join(', ') : 'Inget'
}

// The keep rule's ruleReason codes (app/api/features/admin/duplicates.py).
const RULE_REASON: Record<string, string> = {
  booked: 'Bokad lead vinner',
  both_booked: 'Senast bokad vinner',
  last_worked: 'Senast bearbetad vinner',
  older: 'Äldst vinner',
}

/** "some_code" -> "Some code", for a code no map knows yet. */
function humanise(code: string): string {
  const text = code.replace(/_/g, ' ').trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function ruleReasonLabel(reason: string): string {
  return RULE_REASON[reason] ?? humanise(reason)
}

// Company columns (and the lead's comment) a merge copies onto the keeper.
const FIELD_LABEL: Record<string, string> = {
  name: 'Namn',
  organization_number: 'Org.nr',
  registered_address: 'Adress',
  postal_code: 'Postnummer',
  city: 'Ort',
  country: 'Land',
  employee_count: 'Anställda',
  registration_date: 'Registreringsdatum',
  website_url: 'Webbplats',
  is_active: 'Aktiv',
  is_bankrupt: 'Konkurs',
  is_liquidated: 'Likviderad',
  ai_verdict_score: 'AI-bedömning',
  ai_verdict_status: 'AI-bedömning',
  ai_verdict_generated_at: 'AI-bedömning',
  heuristic_score: 'Heuristisk poäng',
  lead_state: 'Leadstatus',
  contact_quality: 'Kontaktkvalitet',
  export_status: 'Export',
  export_destination: 'Export',
  exported_at: 'Export',
  ingestion_source: 'Källa',
  source_url: 'Källänk',
  status: 'Status',
  lifecycle: 'Kontakta ej',
  do_not_contact: 'Kontakta ej',
  do_not_contact_at: 'Kontakta ej',
  do_not_contact_by: 'Kontakta ej',
  do_not_contact_reason: 'Kontakta ej',
  owner_id: 'Ägare',
  custom: 'Egna fält',
  archived_at: 'Arkiverad',
  comment: 'Kommentar',
}

/** Swedish labels for the filled fields, each label once. */
export function fillLabels(fields: string[]): string[] {
  const labels = fields.map(f => (f.startsWith('sf_') ? 'Salesforce-fält' : FIELD_LABEL[f] ?? humanise(f)))
  return [...new Set(labels)]
}

export interface ImportDuplicateLine {
  key: string
  name: string
  text: string
}

/** Preview rows that name a company the CRM already holds. None of them is created. */
export function importDuplicateLines(report: ImportReport): ImportDuplicateLine[] {
  return (report.rows ?? [])
    .filter(row => row.duplicate)
    .map(row => ({
      key: `${row.sheet}:${row.line}`,
      name: row.name,
      text: `Möjlig dubblett av ${row.existingName || 'ett befintligt företag'}`,
    }))
}

export interface ExistingLead {
  existingId: string
  existingName: string
  owner: string
}

/**
 * The lead a manual add collided with, from the 409 "Finns redan" body. FastAPI
 * puts structured detail under `detail`, so both the flat and nested shapes
 * are read.
 */
export function existingLeadOf(error: unknown): ExistingLead | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const body = error.body as Record<string, unknown> | null | undefined
  const nested = body?.detail
  const data = (nested && typeof nested === 'object' ? nested : body) as Record<string, unknown> | null | undefined
  if (!data || typeof data.existingId !== 'string') return null
  return {
    existingId: data.existingId,
    existingName: typeof data.existingName === 'string' ? data.existingName : '',
    owner: ownerName(data.owner as Parameters<typeof ownerName>[0]),
  }
}

// ── Slå ihop från lista ─────────────────────────────────────────────────────

export const ARROW = ' -> '

/**
 * The pasted text as resolve lines: one per non-blank line, spaces collapsed,
 * and "A->B" or "A → B" written as "A -> B". "id:<companyId>" stays as typed.
 */
export function parseMergeLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map(raw => {
      const parts = raw.split(/->|→/).map(part => part.replace(/\s+/g, ' ').trim())
      if (parts.length === 2 && parts[0] && parts[1]) return parts.join(ARROW)
      return raw.replace(/\s+/g, ' ').trim()
    })
    .filter(Boolean)
}

/** A pasted line as the list shows it: "A -> B" reads "A → B". */
export function lineLabel(line: string): string {
  return line.split(ARROW).join(' → ')
}

export const RESOLVE_STATUS: Record<ResolveStatus, string> = {
  pair: 'Par hittat',
  ambiguous: 'Vilket menas?',
  not_found: 'Hittades inte',
  no_duplicate: 'Ingen dubblett hittad',
  different_legal_entities: DIFFERENT_ENTITIES,
}

export interface AmbiguousGroup {
  role: 'keep' | 'drop'
  forName: string
  options: DuplicateSide[]
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * The choices an ambiguous line needs. In "A -> B" each unclear name picks one
 * company: A's is dropped, B's kept. A single name has to pick both, from the
 * same candidates.
 */
export function ambiguousGroups(item: ResolveItem): AmbiguousGroup[] {
  const groups = item.candidates ?? []
  const names = item.line.split(ARROW)
  if (names.length === 2 && !sameName(names[0], names[1])) {
    return groups.map(group => ({
      role: sameName(group.forName, names[1]) ? 'keep' : 'drop',
      forName: group.forName,
      options: group.options,
    }))
  }
  const only = groups[0]
  if (!only) return []
  return [
    { role: 'drop', forName: only.forName, options: only.options },
    { role: 'keep', forName: only.forName, options: only.options },
  ]
}

export interface AmbiguousPicks {
  keep?: string
  drop?: string
}

/** In the two-sided picker a company picked on one side cannot be picked on the other. */
export function takenByOtherSide(picks: AmbiguousPicks, role: 'keep' | 'drop', companyId: string): boolean {
  return picks[role === 'keep' ? 'drop' : 'keep'] === companyId
}

/** A pair a list row can merge, with its two sides for the cards. */
export interface ListPair {
  ids: MergeIds
  keep?: DuplicateSide
  drop?: DuplicateSide
  /** The preview the resolve sent; a chosen group has none and fetches it. */
  preview?: MergePreview
}

export interface PairOption {
  index: number
  /** "Colliers International ↔ Colliers International AB". */
  label: string
  sides: DuplicateSide[]
  /** Null when the group cannot merge as it stands. */
  pair: ListPair | null
}

/**
 * A plain name's candidates as one choice between pairs, or null for a line
 * whose candidates are per side ("A -> B" with an unclear side).
 */
export function pairOptions(item: ResolveItem): PairOption[] | null {
  const groups = item.candidates ?? []
  if (!groups.some(group => group.pair)) return null
  return groups.map((group, index) => {
    const sideOf = (id: string) => group.options.find(option => option.companyId === id)
    return {
      index,
      label: group.options.map(option => option.name).join(' ↔ '),
      sides: group.options,
      pair: group.pair
        ? { ids: { ...group.pair }, keep: sideOf(group.pair.keepId), drop: sideOf(group.pair.dropId) }
        : null,
    }
  })
}

/** What "Vilket menas?" settled on: an option's index, "none", or nothing yet. */
export type PairChoice = number | 'none' | null

/** The pair a list row merges: the one the resolve found, or the option chosen. */
export function listPair(item: ResolveItem, chosen: PairChoice): ListPair | null {
  if (item.status === 'pair' && item.pair) {
    const { keepId, dropId, explicit, preview } = item.pair
    return { ids: { keepId, dropId, explicit }, keep: preview?.keep, drop: preview?.drop, preview }
  }
  if (item.status !== 'ambiguous' || typeof chosen !== 'number') return null
  return pairOptions(item)?.[chosen]?.pair ?? null
}

/** A group with one candidate needs no choice. */
export function initialPicks(groups: AmbiguousGroup[]): AmbiguousPicks {
  const picks: AmbiguousPicks = {}
  for (const group of groups) {
    if (group.options.length === 1) picks[group.role] = group.options[0].companyId
  }
  return picks
}

/**
 * The line rewritten with the chosen companies as "id:<drop> -> id:<keep>", or
 * null until every choice is made. A name in "A -> B" that was clear keeps its
 * text.
 */
export function rewriteLine(item: ResolveItem, picks: AmbiguousPicks): string | null {
  const groups = ambiguousGroups(item)
  if (groups.length === 0) return null
  if (groups.some(group => !picks[group.role])) return null
  if (picks.keep && picks.keep === picks.drop) return null
  const names = item.line.split(ARROW)
  if (names.length !== 2 && !(picks.drop && picks.keep)) return null
  const drop = picks.drop ? `id:${picks.drop}` : names[0]
  const keep = picks.keep ? `id:${picks.keep}` : names[1]
  if (!drop || !keep) return null
  return `${drop}${ARROW}${keep}`
}

// ── Ångra ───────────────────────────────────────────────────────────────────

export const UNDO_MS = 30_000

/**
 * When the undo bar closes, on this browser's clock. The server's undoUntil
 * caps it, but a clock ahead of the server would hide the bar at once, so a
 * deadline already past falls back to 30 s from the response.
 */
export function undoDeadline(undoUntil: string, receivedAt: number): number {
  const local = receivedAt + UNDO_MS
  const server = Date.parse(undoUntil)
  return server > receivedAt ? Math.min(server, local) : local
}

/** Whole seconds left on the undo countdown, never below zero. */
export function undoSecondsLeft(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000))
}
